import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { FadeUp, SlideLeft, SlideRight } from '../../../components/shared/Motion';
import { EditableText, EditableImage, HrefPill } from '../../components';
import { getIcon } from '../iconMap';

function ProgrammeTabs({ data, onChange }) {
  const items = data.items || [];
  const [tabIdx, setTabIdx] = useState(0);
  const update = (patch) => onChange && onChange({ ...data, ...patch });
  const updateItem = (i, patch) => {
    const next = items.map((x, idx) => (idx === i ? { ...x, ...patch } : x));
    update({ items: next });
  };
  const active = items[tabIdx] || items[0];
  if (!active) return null;
  const ActiveIcon = getIcon(active.icon);

  return (
    <section id="academics" className="relative py-24 lg:py-32 bg-olive-light overflow-hidden">
      <div className="container-page">
        <FadeUp className="text-center max-w-3xl mx-auto mb-14">
          <span className="eyebrow justify-center"><span className="h-px w-8 bg-olive" />
            <EditableText value={data.eyebrow} onChange={(t) => update({ eyebrow: t })} as="span" />
          </span>
          <h2 className="section-title mt-4">
            <EditableText value={data.heading_prefix} onChange={(t) => update({ heading_prefix: t })} as="span" />
            <span className="text-gradient"><EditableText value={data.heading_highlight} onChange={(t) => update({ heading_highlight: t })} as="span" /></span>
            <EditableText value={data.heading_suffix} onChange={(t) => update({ heading_suffix: t })} as="span" />
          </h2>
        </FadeUp>

        <div className="grid lg:grid-cols-12 gap-8 items-stretch">
          <SlideLeft className="lg:col-span-4">
            <div className="relative h-full rounded-3xl bg-gradient-to-b from-olive-deep to-olive-footer p-5 shadow-[0_30px_80px_-30px_rgba(46,58,24,0.55)] overflow-hidden">
              <div className="absolute inset-0 bg-grid-olive [background-size:28px_28px] opacity-15" />
              <div className="relative flex flex-col gap-1.5">
                {items.map((t, i) => {
                  const isActive = tabIdx === i;
                  const TI = getIcon(t.icon);
                  return (
                    <button
                      key={t.key || i}
                      type="button"
                      onClick={() => setTabIdx(i)}
                      className={`group relative flex items-center gap-3 text-left px-5 py-4 rounded-2xl transition-all duration-300 ${
                        isActive
                          ? 'bg-white/10 border-l-4 border-accent translate-x-1.5'
                          : 'border-l-4 border-transparent hover:bg-white/5 hover:translate-x-1.5 hover:border-accent/40'
                      }`}
                    >
                      <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl transition-all ${isActive ? 'bg-accent text-ink' : 'bg-white/10 text-accent'}`}>
                        <TI size={18} />
                      </span>
                      <div>
                        <EditableText
                          value={t.title}
                          onChange={(v) => updateItem(i, { title: v })}
                          as="div"
                          className={`font-display text-lg ${isActive ? 'text-white font-bold' : 'text-white/85 font-semibold'}`}
                        />
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/45 mt-0.5">
                          Programme · {String(i + 1).padStart(2, '0')}
                        </div>
                      </div>
                      {isActive && <ArrowRight size={16} className="ml-auto text-accent" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </SlideLeft>

          <SlideRight className="lg:col-span-8">
            <div className="relative h-full rounded-3xl bg-white shadow-[0_30px_80px_-30px_rgba(46,58,24,0.25)] overflow-hidden border border-olive/10">
              <AnimatePresence mode="wait">
                <motion.div key={active.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
                  <div className="relative h-80 overflow-hidden">
                    <EditableImage
                      src={active.image?.src}
                      alt={active.image?.alt || active.title}
                      onChange={(src, alt) => updateItem(tabIdx, { image: { src, alt } })}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent" />
                    <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between">
                      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent text-ink font-bold text-xs uppercase tracking-[0.2em] shadow-glow">
                        <ActiveIcon size={14} />
                        <EditableText value={active.title} onChange={(v) => updateItem(tabIdx, { title: v })} as="span" />
                      </div>
                      <span className="hidden md:inline text-[10px] font-mono uppercase tracking-[0.3em] text-white/85">
                        {String(tabIdx + 1).padStart(2, '0')} / {String(items.length).padStart(2, '0')}
                      </span>
                    </div>
                  </div>
                  <div className="p-8">
                    <EditableText value={active.title} onChange={(v) => updateItem(tabIdx, { title: v })} as="h3" className="font-display text-2xl lg:text-3xl text-ink leading-tight" />
                    <EditableText value={active.description} onChange={(v) => updateItem(tabIdx, { description: v })} as="p" multiline className="mt-4 text-ink/70 leading-relaxed text-lg" />
                    <Link to={data.explore_cta?.href || '#'} className="mt-6 inline-flex items-center gap-2 text-olive font-semibold text-sm group">
                      <EditableText
                        value={data.explore_cta?.label || 'Explore the programme'}
                        onChange={(t) => update({ explore_cta: { ...(data.explore_cta || {}), label: t } })}
                        as="span"
                      />
                      <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                    </Link>
                    <HrefPill value={data.explore_cta?.href} onChange={(h) => update({ explore_cta: { ...(data.explore_cta || {}), href: h } })} />
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </SlideRight>
        </div>
      </div>
    </section>
  );
}

export default ProgrammeTabs;
